import React from 'react';
import { useHistory } from 'react-router-dom';
import { isEmpty } from 'lodash';

import FormFooter from 'components/FormFooter';
import { CompanyFieldsNames } from 'components/CreateComps/FormSection/CompanyForm/constants';
import CompanySearchCombo from 'components/CreateComps/CompanySearchCombo';
import {
  I18N_AVANT_PLATFORM_PROPERTY_PROMPT_PATH,
  I18N_PLATFORM_COMMON_WORD_PATH,
} from 'constants/i18n';
import locations from 'routes';
import { ICompany } from 'interfaces/ICompany';
import { translateText } from 'utils/i18n';

import formsStyles from '../FormSection.module.scss';
import { CompanyBasicInfoSection } from './CompanyBasicInfoSection';
import { CompanyLocationSection } from './CompanyLocationSection';
import { CompanyContactSection } from './CompanyContactSection';
import { useValidateCompany } from './useValidateCompany';

export interface Props {
  company: ICompany;
  isLoading: boolean;
  onChange: (field: string, value: any) => void;
  onSubmit: () => void;
  showCompanyAliasesButton?: boolean;
  onCancel?: () => void;
  onOpenAliases?: () => void;
  setCompanyLogoRemoved?: (removed: boolean) => void;
  setCompanyLogoToUpload?: (file?: File) => void;
}

const CompanyForm: React.FC<Props> = ({
  company,
  isLoading,
  onCancel,
  onChange,
  onOpenAliases,
  onSubmit,
  setCompanyLogoRemoved,
  setCompanyLogoToUpload,
  showCompanyAliasesButton,
}) => {
  const history = useHistory();

  const { variables, handlers } = useValidateCompany({ company });

  const handleSubmit = async () => {
    const hasErrors = await handlers.validateCompanyName([
      CompanyFieldsNames.OPERATING_NAME,
      CompanyFieldsNames.LEGAL_NAME,
    ]);

    if (isEmpty(hasErrors)) onSubmit();
  };

  const handleOnCancel = () =>
    onCancel ? onCancel() : history.push(locations.root());

  return (
    <>
      <form>
        <div className={formsStyles['content']}>
          <div className={formsStyles.container}>
            <CompanyBasicInfoSection
              company={company}
              fieldsErrors={variables.fieldsErrors}
              setFieldsErrors={handlers.setFieldsErrors}
              onChange={onChange}
              showCompanyAliasesButton={showCompanyAliasesButton}
              setCompanyLogoRemoved={setCompanyLogoRemoved}
              setCompanyLogoToUpload={setCompanyLogoToUpload}
              onOpenAliases={onOpenAliases}
            />
            <CompanyLocationSection company={company} onChange={onChange} />
            <CompanyContactSection company={company} onChange={onChange} />
          </div>

          <div className={formsStyles.container}>
            <CompanySearchCombo
              searchOnlyCompanies
              aliasPlaceholder={suggestionName => {
                const clickToTagATenant = `${I18N_PLATFORM_COMMON_WORD_PATH}.clickToTagATenant`;
                return `${translateText(clickToTagATenant, {
                  suggestionName: suggestionName,
                })}.`;
              }}
              label={translateText(
                `${I18N_AVANT_PLATFORM_PROPERTY_PROMPT_PATH}.parentCompany`,
              )}
              btnLabel={translateText(
                `${I18N_PLATFORM_COMMON_WORD_PATH}.createCompany`,
              )}
              placeholder={translateText(
                `${I18N_AVANT_PLATFORM_PROPERTY_PROMPT_PATH}.enterParentCompany`,
              )}
              wrapperClassName={formsStyles['form-row']}
              inputId="parent-company-id"
              companies={company.parentCompany ? [company.parentCompany] : []}
              onClearSelectedSuggestion={() => {
                onChange(CompanyFieldsNames.PARENT_COMPANY_ID, null);
                onChange(CompanyFieldsNames.PARENT_COMPANY, null);
              }}
              onCompanySelection={(value: any) => {
                const isCustom = !!value?.custom;
                const id = isCustom
                  ? value.custom?.id
                  : value?.companyId ?? null;

                onChange(CompanyFieldsNames.PARENT_COMPANY_ID, id);
                if (isCustom)
                  onChange(CompanyFieldsNames.PARENT_COMPANY, value.custom);
              }}
            />
          </div>
        </div>
        <FormFooter
          onSubmit={handleSubmit}
          onCancel={handleOnCancel}
          isCancelDisabled={isLoading}
          isSubmitDisabled={
            !company.name || isLoading || variables.hasValidationError
          }
        />
      </form>
    </>
  );
};

export default CompanyForm;
